import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import PrintActions from '@/shared/components/PrintActions';
import { useQuery } from '@tanstack/react-query';
import { payrolls } from '@/api/payrolls';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { partyCodesText } from '@/shared/utils/partyCodes';

const fmt = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MONTHS_UK = ['', 'січень', 'лютий', 'березень', 'квітень', 'травень',
  'червень', 'липень', 'серпень', 'вересень', 'жовтень', 'листопад', 'грудень'];

type Form = 'vedomost' | 'listok' | 'p6';

export default function PayrollPrintPage() {
  const { id } = useParams();
  // ч.761: ?form=vedomost|listok|p6 — з «друк ▾» редактора: один документ на форму
  // (перемикач форм схований), + редактор друку з печаткою/підписом і збереженням у вкладення.
  const [searchParams] = useSearchParams();
  const fixedForm = searchParams.get('form') as Form | null;
  const [formType, setFormType] = useState<Form>(fixedForm ?? 'vedomost');
  const [selectedEmp, setSelectedEmp] = useState<string>('');

  const docQ = useQuery({
    queryKey: ['payroll', id ?? ''],
    queryFn: () => payrolls.get(id!),
    enabled: !!id,
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const empQ = useQuery({
    queryKey: ['employees', docQ.data?.organization_id ?? ''],
    queryFn: () => refs.employees(docQ.data!.organization_id),
    enabled: !!docQ.data,
  });

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const empName = useMemo(() => {
    const m = new Map<string, { name: string; code: string | null; position?: string | null }>();
    empQ.data?.forEach((e) => m.set(e.id, { name: e.name, code: e.code, position: e.position_name }));
    return m;
  }, [empQ.data]);

  if (!docQ.data) return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  const doc = docQ.data;
  const org = orgsQ.data?.items.find((o) => o.id === doc.organization_id);
  const periodLabel = `${MONTHS_UK[doc.period_month]} ${doc.period_year}`;

  return (
    <div className="print-page" style={{
      padding: '15mm', fontFamily: 'Times, serif', fontSize: '10pt', lineHeight: 1.3,
      maxWidth: '297mm', margin: '0 auto', background: 'white',
    }}>
      {/* Перемикач — приховується при друку */}
      <div className="print-hide" style={{
        position: 'sticky', top: 0, background: '#f1f5f9',
        padding: '0.5rem 1rem', marginBottom: '1rem', borderRadius: 6,
        display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap',
      }}>
        {!fixedForm && <strong>Форма:</strong>}
        {!fixedForm && <button onClick={() => setFormType('vedomost')}
                style={{ background: formType === 'vedomost' ? '#3b82f6' : '#e2e8f0',
                         color: formType === 'vedomost' ? 'white' : 'inherit' }}>
          Розрахункова відомість
        </button>}
        {!fixedForm && <button onClick={() => setFormType('listok')}
                style={{ background: formType === 'listok' ? '#3b82f6' : '#e2e8f0',
                         color: formType === 'listok' ? 'white' : 'inherit' }}>
          Розрахунковий листок
        </button>}
        {!fixedForm && <button onClick={() => setFormType('p6')}
                style={{ background: formType === 'p6' ? '#3b82f6' : '#e2e8f0',
                         color: formType === 'p6' ? 'white' : 'inherit' }}>
          П-6 (табель)
        </button>}
        {fixedForm && (
          <strong>{fixedForm === 'vedomost' ? 'Розрахункова відомість' : fixedForm === 'listok' ? 'Розрахунковий листок' : 'П-6 (розрахунок)'}</strong>
        )}
        {formType === 'listok' && (
          <select value={selectedEmp} onChange={(e) => setSelectedEmp(e.target.value)}>
            <option value="">— оберіть співробітника —</option>
            {doc.items.map((it) => {
              const emp = empName.get(it.employee_id);
              return (
                <option key={it.id} value={it.employee_id}>
                  {emp?.name ?? '—'}{emp?.code ? ` — ${emp.code}` : ''}
                </option>
              );
            })}
          </select>
        )}
      </div>

      <PrintActions docMeta={{
        organizationId: doc.organization_id,
        documentType: 'doc.payrolls',
        documentId: doc.id,
        filename: `${formType === 'vedomost' ? 'Розрахункова відомість' : formType === 'listok' ? 'Розрахунковий листок' : 'П-6'} ${doc.document_number}`,
        orgName: org?.name ?? null,
      }} />

      {/* Шапка — спільна для всіх форм */}
      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
        <div style={{ fontSize: '10pt' }}>{org?.full_name ?? org?.name ?? ''}</div>
        {partyCodesText(org) && (
          <div style={{ fontSize: '9pt', color: '#555' }}>{partyCodesText(org)}</div>
        )}
      </div>

      {formType === 'vedomost' && (
        <>
          <h2 style={{ textAlign: 'center', fontSize: '13pt', margin: '0.5rem 0' }}>
            РОЗРАХУНКОВА ВІДОМІСТЬ ЗАРОБІТНОЇ ПЛАТИ
            {doc.payroll_kind === 'ADVANCE' ? ' (аванс)' : ''}
          </h2>
          <p style={{ textAlign: 'center', margin: '0.3rem 0' }}>
            за {periodLabel} р.
          </p>
          <p style={{ textAlign: 'center', fontSize: '9pt', color: '#666' }}>
            № документа: {doc.document_number}
          </p>

          <table style={{
            width: '100%', borderCollapse: 'collapse', fontSize: '9pt', marginTop: '1rem',
            border: '1px solid #000',
          }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ border: '1px solid #000', padding: '0.2rem' }}>№</th>
                <th style={{ border: '1px solid #000', padding: '0.2rem' }}>Таб. №</th>
                <th style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'left' }}>
                  ПІБ
                </th>
                <th style={{ border: '1px solid #000', padding: '0.2rem' }}>Оклад</th>
                <th style={{ border: '1px solid #000', padding: '0.2rem' }}>Премія</th>
                <th style={{ border: '1px solid #000', padding: '0.2rem' }}>Брутто</th>
                <th style={{ border: '1px solid #000', padding: '0.2rem' }}>ПДФО</th>
                <th style={{ border: '1px solid #000', padding: '0.2rem' }}>ВЗ</th>
                <th style={{ border: '1px solid #000', padding: '0.2rem' }}>ЄСВ</th>
                <th style={{ border: '1px solid #000', padding: '0.2rem' }}>Аванс</th>
                <th style={{ border: '1px solid #000', padding: '0.2rem' }}>До виплати</th>
              </tr>
            </thead>
            <tbody>
              {doc.items.map((it) => {
                const emp = empName.get(it.employee_id);
                return (
                  <tr key={it.id}>
                    <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'center' }}>
                      {it.line_number}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'center' }}>
                      {emp?.code ?? '—'}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '0.2rem' }}>{emp?.name ?? '—'}</td>
                    <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'right' }}>
                      {fmt(it.salary_amount ?? '0')}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'right' }}>
                      {fmt(it.bonus_amount ?? '0')}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'right', fontWeight: 600 }}>
                      {fmt(it.gross_amount)}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'right' }}>
                      {fmt(it.pdfo_amount)}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'right' }}>
                      {fmt(it.wz_amount)}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'right' }}>
                      {fmt(it.esv_amount)}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'right' }}>
                      {fmt(it.advance_amount ?? '0')}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'right', fontWeight: 600 }}>
                      {fmt(it.net_amount)}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ fontWeight: 700, background: '#f8fafc' }}>
                <td colSpan={3} style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'right' }}>
                  Усього:
                </td>
                <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'right' }}>
                  {fmt(doc.items.reduce((s, e) => s + Number(e.salary_amount || 0), 0))}
                </td>
                <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'right' }}>
                  {fmt(doc.items.reduce((s, e) => s + Number(e.bonus_amount || 0), 0))}
                </td>
                <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'right' }}>
                  {fmt(doc.total_gross)}
                </td>
                <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'right' }}>
                  {fmt(doc.total_pdfo)}
                </td>
                <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'right' }}>
                  {fmt(doc.total_wz)}
                </td>
                <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'right' }}>
                  {fmt(doc.total_esv)}
                </td>
                <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'right' }}>
                  {fmt(doc.items.reduce((s, e) => s + Number(e.advance_amount || 0), 0))}
                </td>
                <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'right' }}>
                  {fmt(doc.total_net)}
                </td>
              </tr>
            </tbody>
          </table>

          <table style={{ width: '100%', marginTop: '2rem' }}>
            <tbody>
              <tr>
                <td>
                  <div>Керівник:</div>
                  <div style={{ marginTop: '1.5rem', borderTop: '1px solid #000', textAlign: 'center', width: '60%' }}>
                    ___________________
                  </div>
                  <div style={{ fontSize: '8pt', textAlign: 'center', width: '60%' }}>(підпис, П.І.Б.)</div>
                </td>
                <td>
                  <div>Головний бухгалтер:</div>
                  <div style={{ marginTop: '1.5rem', borderTop: '1px solid #000', textAlign: 'center', width: '60%' }}>
                    ___________________
                  </div>
                  <div style={{ fontSize: '8pt', textAlign: 'center', width: '60%' }}>(підпис, П.І.Б.)</div>
                </td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {formType === 'listok' && (() => {
        const item = doc.items.find((it) => it.employee_id === selectedEmp);
        if (!item) {
          return (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
              Оберіть співробітника у фільтрі вгорі.
            </div>
          );
        }
        const emp = empName.get(item.employee_id);
        return (
          <>
            <h2 style={{ textAlign: 'center', fontSize: '13pt', margin: '0.5rem 0' }}>
              РОЗРАХУНКОВИЙ ЛИСТОК
            </h2>
            <p style={{ textAlign: 'center', margin: '0.3rem 0' }}>
              за {periodLabel} р.
            </p>

            <table style={{ width: '100%', marginTop: '1rem', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '0.3rem' }}><strong>Прізвище, ім'я, по батькові:</strong></td>
                  <td style={{ padding: '0.3rem' }}>{emp?.name ?? '—'}</td>
                </tr>
                <tr>
                  <td style={{ padding: '0.3rem' }}><strong>Табельний номер:</strong></td>
                  <td style={{ padding: '0.3rem' }}>{emp?.code ?? '—'}</td>
                </tr>
                {emp?.position && (
                  <tr>
                    <td style={{ padding: '0.3rem' }}><strong>Посада:</strong></td>
                    <td style={{ padding: '0.3rem' }}>{emp.position}</td>
                  </tr>
                )}
              </tbody>
            </table>

            <h3 style={{ marginTop: '1.5rem', fontSize: '11pt' }}>Нараховано:</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000' }}>
              <tbody>
                <tr>
                  <td style={{ border: '1px solid #000', padding: '0.3rem' }}>Оклад / тариф</td>
                  <td style={{ border: '1px solid #000', padding: '0.3rem', textAlign: 'right', width: '30%' }}>
                    {fmt(item.salary_amount ?? '0')}
                  </td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid #000', padding: '0.3rem' }}>Премія</td>
                  <td style={{ border: '1px solid #000', padding: '0.3rem', textAlign: 'right' }}>
                    {fmt(item.bonus_amount ?? '0')}
                  </td>
                </tr>
                <tr style={{ fontWeight: 700, background: '#f8fafc' }}>
                  <td style={{ border: '1px solid #000', padding: '0.3rem' }}>Брутто (всього нараховано)</td>
                  <td style={{ border: '1px solid #000', padding: '0.3rem', textAlign: 'right' }}>
                    {fmt(item.gross_amount)}
                  </td>
                </tr>
              </tbody>
            </table>

            <h3 style={{ marginTop: '1.5rem', fontSize: '11pt' }}>Утримано:</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000' }}>
              <tbody>
                <tr>
                  <td style={{ border: '1px solid #000', padding: '0.3rem' }}>
                    ПДФО ({doc.pdfo_rate}%)
                  </td>
                  <td style={{ border: '1px solid #000', padding: '0.3rem', textAlign: 'right', width: '30%' }}>
                    {fmt(item.pdfo_amount)}
                  </td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid #000', padding: '0.3rem' }}>
                    Військовий збір ({doc.wz_rate}%)
                  </td>
                  <td style={{ border: '1px solid #000', padding: '0.3rem', textAlign: 'right' }}>
                    {fmt(item.wz_amount)}
                  </td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid #000', padding: '0.3rem' }}>Виплачений аванс</td>
                  <td style={{ border: '1px solid #000', padding: '0.3rem', textAlign: 'right' }}>
                    {fmt(item.advance_amount ?? '0')}
                  </td>
                </tr>
                <tr style={{ fontWeight: 700, background: '#f8fafc' }}>
                  <td style={{ border: '1px solid #000', padding: '0.3rem' }}>Всього утримано / виплачено</td>
                  <td style={{ border: '1px solid #000', padding: '0.3rem', textAlign: 'right' }}>
                    {fmt(
                      Number(item.pdfo_amount) +
                      Number(item.wz_amount) +
                      Number(item.advance_amount ?? '0')
                    )}
                  </td>
                </tr>
              </tbody>
            </table>

            <table style={{
              width: '100%', borderCollapse: 'collapse', marginTop: '1rem',
              border: '2px solid #000',
            }}>
              <tbody>
                <tr style={{ fontSize: '12pt', fontWeight: 700 }}>
                  <td style={{ border: '1px solid #000', padding: '0.5rem' }}>
                    ДО ВИПЛАТИ
                  </td>
                  <td style={{ border: '1px solid #000', padding: '0.5rem', textAlign: 'right', width: '30%' }}>
                    {fmt(item.net_amount)} грн
                  </td>
                </tr>
              </tbody>
            </table>

            <p style={{ marginTop: '2rem', fontSize: '9pt' }}>
              Довідково: нарахування ЄСВ працедавцем ({doc.esv_rate}%) — {fmt(item.esv_amount)} грн
              (не утримується з заробітку, сплачується за рахунок роботодавця).
            </p>

            <table style={{ width: '100%', marginTop: '3rem' }}>
              <tbody>
                <tr>
                  <td>
                    <div>Працівник:</div>
                    <div style={{ marginTop: '1.5rem', borderTop: '1px solid #000', textAlign: 'center', width: '60%' }}>
                      ___________________
                    </div>
                    <div style={{ fontSize: '8pt', textAlign: 'center', width: '60%' }}>(підпис)</div>
                  </td>
                  <td>
                    <div>Головний бухгалтер:</div>
                    <div style={{ marginTop: '1.5rem', borderTop: '1px solid #000', textAlign: 'center', width: '60%' }}>
                      ___________________
                    </div>
                    <div style={{ fontSize: '8pt', textAlign: 'center', width: '60%' }}>(підпис, П.І.Б.)</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </>
        );
      })()}

      {formType === 'p6' && (
        <>
          <h2 style={{ textAlign: 'center', fontSize: '12pt', margin: '0.5rem 0' }}>
            ТИПОВА ФОРМА № П-6
          </h2>
          <p style={{ textAlign: 'center', margin: '0.3rem 0', fontSize: '10pt' }}>
            Розрахунок заробітної плати
          </p>
          <p style={{ textAlign: 'center', margin: '0.3rem 0' }}>
            за {periodLabel} р.
          </p>

          <table style={{
            width: '100%', borderCollapse: 'collapse', fontSize: '9pt', marginTop: '1rem',
            border: '1px solid #000',
          }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th rowSpan={2} style={{ border: '1px solid #000', padding: '0.2rem' }}>№</th>
                <th rowSpan={2} style={{ border: '1px solid #000', padding: '0.2rem' }}>Таб.№</th>
                <th rowSpan={2} style={{ border: '1px solid #000', padding: '0.2rem' }}>ПІБ</th>
                <th colSpan={2} style={{ border: '1px solid #000', padding: '0.2rem' }}>Нараховано</th>
                <th colSpan={3} style={{ border: '1px solid #000', padding: '0.2rem' }}>Утримано</th>
                <th rowSpan={2} style={{ border: '1px solid #000', padding: '0.2rem' }}>
                  Сума до видачі
                </th>
                <th rowSpan={2} style={{ border: '1px solid #000', padding: '0.2rem' }}>Підпис</th>
              </tr>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ border: '1px solid #000', padding: '0.2rem' }}>Оклад</th>
                <th style={{ border: '1px solid #000', padding: '0.2rem' }}>Премія</th>
                <th style={{ border: '1px solid #000', padding: '0.2rem' }}>ПДФО</th>
                <th style={{ border: '1px solid #000', padding: '0.2rem' }}>ВЗ</th>
                <th style={{ border: '1px solid #000', padding: '0.2rem' }}>Аванс</th>
              </tr>
            </thead>
            <tbody>
              {doc.items.map((it) => {
                const emp = empName.get(it.employee_id);
                return (
                  <tr key={it.id}>
                    <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'center' }}>
                      {it.line_number}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'center' }}>
                      {emp?.code ?? '—'}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '0.2rem' }}>{emp?.name ?? '—'}</td>
                    <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'right' }}>
                      {fmt(it.salary_amount ?? '0')}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'right' }}>
                      {fmt(it.bonus_amount ?? '0')}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'right' }}>
                      {fmt(it.pdfo_amount)}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'right' }}>
                      {fmt(it.wz_amount)}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'right' }}>
                      {fmt(it.advance_amount ?? '0')}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '0.2rem', textAlign: 'right', fontWeight: 600 }}>
                      {fmt(it.net_amount)}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '0.2rem', minWidth: '80px' }}></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      <style>{`
        @media print {
          .print-hide { display: none !important; }
        }
      `}</style>
    </div>
  );
}
